import { formatEmployeeTimeProjectNumber } from "@/lib/auftragAutocomplete";
import { formatDate } from "@/lib/format";
import {
  formatHours,
  type EmployeeTimeAccount,
  type EmployeeTimeEntry,
  type EmployeeTimeMonthProject,
} from "@/lib/employeeTimes";

export const ZEITERFASSUNG_ACTION_COL_WIDTH = 44;
export const ZE_COL_MIN_WIDTH = 72;
export const ZE_COL_MAX_WIDTH = 520;

export function clampZeiterfassungColumnWidth(width: number): number {
  return Math.min(ZE_COL_MAX_WIDTH, Math.max(ZE_COL_MIN_WIDTH, Math.round(width)));
}

/* ── Übersicht (Einzel-Einträge) ── */

export type EntriesOverviewColumnKey =
  | "work_date"
  | "employee"
  | "project"
  | "hours"
  | "time_range"
  | "entry_type"
  | "homeoffice"
  | "comment";

export const ENTRIES_OVERVIEW_COLUMNS: EntriesOverviewColumnKey[] = [
  "work_date",
  "employee",
  "project",
  "hours",
  "time_range",
  "entry_type",
  "homeoffice",
  "comment",
];

const ENTRIES_DEFAULT_WIDTHS: Record<EntriesOverviewColumnKey, number> = {
  work_date: 110,
  employee: 160,
  project: 240,
  hours: 90,
  time_range: 120,
  entry_type: 100,
  homeoffice: 110,
  comment: 200,
};

export function getEntriesColumnLabel(key: EntriesOverviewColumnKey): string {
  switch (key) {
    case "work_date":
      return "Datum";
    case "employee":
      return "Mitarbeiter";
    case "project":
      return "Projekt";
    case "hours":
      return "Stunden";
    case "time_range":
      return "Zeit";
    case "entry_type":
      return "Art";
    case "homeoffice":
      return "Homeoffice";
    case "comment":
      return "Kommentar";
    default:
      return key;
  }
}

export function getEntriesColumnDefaultWidth(key: string): number {
  return ENTRIES_DEFAULT_WIDTHS[key as EntriesOverviewColumnKey] ?? 120;
}

const ENTRY_TYPE_LABELS: Record<string, string> = {
  manual: "Von–Bis",
  timer: "Timer",
  range: "Von–Bis",
  break: "Pause",
};

export function formatEntryTypeLabel(entryType: string): string {
  return ENTRY_TYPE_LABELS[entryType] || entryType;
}

export function formatEntryProjectLabel(entry: EmployeeTimeEntry): string {
  return formatEmployeeTimeProjectNumber(entry);
}

export function formatEntryProjectSubtitle(entry: EmployeeTimeEntry): string {
  const parts = [entry.beschreibung, entry.kunde, entry.ort_zusatz || entry.ort].filter(Boolean);
  if (entry.position_bezeichnung || entry.position_nr != null) {
    const nr =
      entry.position_nr != null ? `Pos. ${entry.position_nr}` : "Pos.";
    const name = String(entry.position_bezeichnung || "").trim();
    parts.push(name ? `${nr} – ${name}` : nr);
  }
  return parts.join(" · ");
}

export function formatEntryTimeRange(entry: EmployeeTimeEntry): string {
  if (entry.time_from && entry.time_to) {
    return `${entry.time_from.slice(0, 5)} – ${entry.time_to.slice(0, 5)}`;
  }
  return "";
}

/* ── Stundenkonten ── */

export type AccountsColumnKey =
  | "employee"
  | "login"
  | "month_hours"
  | "total_hours"
  | "entry_count"
  | "project_count";

export const ACCOUNTS_COLUMNS: AccountsColumnKey[] = [
  "employee",
  "login",
  "month_hours",
  "total_hours",
  "entry_count",
  "project_count",
];

const ACCOUNTS_DEFAULT_WIDTHS: Record<AccountsColumnKey, number> = {
  employee: 180,
  login: 140,
  month_hours: 120,
  total_hours: 120,
  entry_count: 110,
  project_count: 110,
};

export function getAccountsColumnLabel(key: AccountsColumnKey): string {
  switch (key) {
    case "employee":
      return "Mitarbeiter";
    case "login":
      return "Login";
    case "month_hours":
      return "Monat";
    case "total_hours":
      return "Gesamt";
    case "entry_count":
      return "Einträge";
    case "project_count":
      return "Projekte";
    default:
      return key;
  }
}

export function getAccountsColumnDefaultWidth(key: string): number {
  return ACCOUNTS_DEFAULT_WIDTHS[key as AccountsColumnKey] ?? 120;
}

export function renderAccountsCell(account: EmployeeTimeAccount, col: AccountsColumnKey): string {
  switch (col) {
    case "employee":
      return account.user_display || account.user_login;
    case "login":
      return account.user_login;
    case "month_hours":
      return formatHours(account.month_hours);
    case "total_hours":
      return formatHours(account.total_hours);
    case "entry_count":
      return String(account.entry_count);
    case "project_count":
      return String(account.project_count);
    default:
      return "";
  }
}

/* ── Monat nach Projekt ── */

export type MonthProjectColumnKey =
  | "project"
  | "hours"
  | "entry_count";

export const MONTH_PROJECT_COLUMNS: MonthProjectColumnKey[] = [
  "project",
  "hours",
  "entry_count",
];

const MONTH_PROJECT_DEFAULT_WIDTHS: Record<MonthProjectColumnKey, number> = {
  project: 320,
  hours: 110,
  entry_count: 110,
};

export function getMonthProjectColumnLabel(key: MonthProjectColumnKey): string {
  switch (key) {
    case "project":
      return "Projekt";
    case "hours":
      return "Stunden";
    case "entry_count":
      return "Einträge";
    default:
      return key;
  }
}

export function getMonthProjectColumnDefaultWidth(key: string): number {
  return MONTH_PROJECT_DEFAULT_WIDTHS[key as MonthProjectColumnKey] ?? 120;
}

export function formatMonthProjectLabel(proj: EmployeeTimeMonthProject): string {
  return formatEmployeeTimeProjectNumber(proj);
}

export function formatMonthProjectSubtitle(proj: EmployeeTimeMonthProject): string {
  return [proj.beschreibung, proj.kunde].filter(Boolean).join(" · ");
}

/* ── Arbeitszeitentabelle (Monat) ── */

export type WorkTimeDayColumnKey =
  | "date"
  | "hinweis"
  | "soll"
  | "ist"
  | "saldo"
  | "stand";

/** @deprecated Alias – gleiche Spalten wie WorkTimeDay. */
export type MonthWorkTimeDayColumnKey = WorkTimeDayColumnKey;

export const WORK_TIME_DAY_COLUMNS: WorkTimeDayColumnKey[] = [
  "date",
  "hinweis",
  "soll",
  "ist",
  "saldo",
  "stand",
];

export const MONTH_WORK_TIME_DAY_COLUMNS = WORK_TIME_DAY_COLUMNS;

const WORK_TIME_DAY_DEFAULT_WIDTHS: Record<WorkTimeDayColumnKey, number> = {
  date: 170,
  hinweis: 160,
  soll: 90,
  ist: 90,
  saldo: 100,
  stand: 120,
};

export function getWorkTimeDayColumnLabel(key: WorkTimeDayColumnKey): string {
  switch (key) {
    case "date":
      return "Datum";
    case "hinweis":
      return "Hinweis";
    case "soll":
      return "Soll";
    case "ist":
      return "Ist";
    case "saldo":
      return "Saldo";
    case "stand":
      return "Aktueller Stand";
    default:
      return key;
  }
}

export const getMonthWorkTimeDayColumnLabel = getWorkTimeDayColumnLabel;

export function getWorkTimeDayColumnDefaultWidth(key: string): number {
  return WORK_TIME_DAY_DEFAULT_WIDTHS[key as WorkTimeDayColumnKey] ?? 120;
}

export const getMonthWorkTimeDayColumnDefaultWidth = getWorkTimeDayColumnDefaultWidth;

/* ── Tageserfassung ── */

export type DayEntryColumnKey =
  | "project"
  | "position"
  | "time_from"
  | "duration"
  | "time_to"
  | "comment";

export const DAY_ENTRY_COLUMNS: DayEntryColumnKey[] = [
  "project",
  "position",
  "time_from",
  "duration",
  "time_to",
  "comment",
];

const DAY_ENTRY_DEFAULT_WIDTHS: Record<DayEntryColumnKey, number> = {
  project: 220,
  position: 200,
  time_from: 110,
  duration: 90,
  time_to: 110,
  comment: 180,
};

export function getDayEntryColumnLabel(
  key: DayEntryColumnKey,
  projectRequired?: boolean,
): string {
  switch (key) {
    case "project":
      return projectRequired ? "Projekt *" : "Projekt";
    case "position":
      return "Position";
    case "time_from":
      return "Von";
    case "duration":
      return "Dauer";
    case "time_to":
      return "Bis";
    case "comment":
      return "Kommentar";
    default:
      return key;
  }
}

export function getDayEntryColumnDefaultWidth(key: string): number {
  return DAY_ENTRY_DEFAULT_WIDTHS[key as DayEntryColumnKey] ?? 120;
}

export function formatEntryDateLabel(iso: string): string {
  return formatDate(iso);
}
